import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'

// ── Types ──

export interface EmAccount {
  id: string; name: string; email: string; reply_to?: string; sender_name: string
  smtp_host: string; smtp_port: number; smtp_user: string
  use_tls: boolean; use_ssl: boolean; daily_limit: number; sent_today: number
  is_active: boolean; is_default: boolean
  signature_html?: string | null
  signature_image_url?: string | null
  created_at: string; updated_at: string
}

export interface EmTemplate {
  id: string; name: string; subject: string; body_html: string; body_text?: string
  variables?: string[]; category?: string; is_active: boolean
  include_signature?: boolean
  og_image?: string | null
  og_description?: string | null
  whatsapp_message?: string | null
  whatsapp_link_message?: string | null
  created_at: string; updated_at: string
}

export interface EmContactList {
  id: string; name: string; description?: string; contact_count: number
  is_active: boolean; created_at: string; updated_at: string
}

export interface EmContact {
  id: string; list_id: string; first_name: string; last_name?: string
  email: string; whatsapp?: string; company_cnpj?: string; company_name?: string
  curva_abc?: string; cnpj?: string; segmento?: string
  is_valid: boolean; is_unsubscribed: boolean; created_at: string
}

export interface EmCampaign {
  id: string; name: string; template_id: string; list_id: string; account_id?: string
  status: string; channels: string
  scheduled_start?: string; scheduled_end?: string; throttle_per_min: number
  total_recipients: number; sent_count: number; failed_count: number
  opened_count: number; clicked_count: number
  whatsapp_account_id?: string; whatsapp_message?: string
  wa_sent_count: number; wa_delivered_count: number; wa_read_count: number; wa_failed_count: number
  created_at: string; updated_at: string
  template_name?: string; list_name?: string; account_name?: string
}

export interface EmCampaignLog {
  id: string; campaign_id: string; contact_id?: string; email_to: string
  subject?: string; status: string; sent_at?: string; opened_at?: string
  clicked_at?: string; error_message?: string; opened_count: number
}

export interface EmWhatsAppLog {
  id: string; campaign_id: string; contact_id?: string; phone_to: string
  message_text?: string; status: string; sent_at?: string; delivered_at?: string
  read_at?: string; error_message?: string
}

export interface EmWhatsAppAccount {
  id: string; name: string; phone_number?: string; instance_id: string
  daily_limit: number; sent_today: number; delay_ms: number
  is_connected: boolean; is_active: boolean; created_at: string; updated_at: string
}

export interface EmDashboard {
  total_campaigns: number; total_emails_sent: number; total_wa_sent: number
  open_rate: number; click_rate: number; bounce_rate: number
  wa_delivery_rate: number; wa_read_rate: number
  active_lists: number; total_contacts: number
}

export interface CsvUploadResponse {
  columns: string[]; sample_rows: Record<string, string>[]; total_rows: number
}

// ── Hooks ──

const BASE = '/v1/email-marketing'

// Dashboard
export function useEmDashboard() {
  return useQuery({ queryKey: ['em-dashboard'], queryFn: async () => { const { data } = await api.get<EmDashboard>(`${BASE}/dashboard`); return data }, staleTime: 30000 })
}

// Accounts
export function useEmAccounts() {
  return useQuery({ queryKey: ['em-accounts'], queryFn: async () => { const { data } = await api.get<EmAccount[]>(`${BASE}/accounts`); return data }, staleTime: 60000 })
}
export function useCreateEmAccount() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async (body: any) => { const { data } = await api.post(`${BASE}/accounts`, body); return data }, onSuccess: () => qc.invalidateQueries({ queryKey: ['em-accounts'] }) })
}
export function useUpdateEmAccount() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async ({ id, ...body }: any) => { const { data } = await api.put(`${BASE}/accounts/${id}`, body); return data }, onSuccess: () => qc.invalidateQueries({ queryKey: ['em-accounts'] }) })
}
export function useDeleteEmAccount() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async (id: string) => { await api.delete(`${BASE}/accounts/${id}`) }, onSuccess: () => qc.invalidateQueries({ queryKey: ['em-accounts'] }) })
}
export function useTestEmAccount() {
  return useMutation({ mutationFn: async (id: string) => { const { data } = await api.post(`${BASE}/accounts/${id}/test`); return data } })
}
export function useSyncEmAccountFromUserSmtp() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => { const { data } = await api.post(`${BASE}/accounts/${id}/sync-from-user-smtp`); return data },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['em-accounts'] }),
  })
}
export function useCreateEmAccountFromUserSmtp() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: { name: string; sender_name?: string; reply_to?: string; daily_limit?: number; is_default?: boolean }) => {
      const { data } = await api.post(`${BASE}/accounts/from-user-smtp`, body); return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['em-accounts'] }),
  })
}
export function useUserSmtpStatus() {
  return useQuery({
    queryKey: ['user-smtp-status'],
    queryFn: async () => { const { data } = await api.get<{ configured: boolean; smtp_host?: string; smtp_username?: string; smtp_from_name?: string; signature_html?: string | null; signature_image_url?: string | null }>(`/v1/users/me/smtp`); return data },
    staleTime: 30000,
  })
}

// Templates
export function useEmTemplates(params?: { search?: string; category?: string }) {
  return useQuery({ queryKey: ['em-templates', params], queryFn: async () => { const { data } = await api.get<EmTemplate[]>(`${BASE}/templates`, { params }); return data }, staleTime: 30000 })
}
export function useEmTemplate(id: string) {
  return useQuery({ queryKey: ['em-template', id], queryFn: async () => { const { data } = await api.get<EmTemplate>(`${BASE}/templates/${id}`); return data }, enabled: !!id })
}
export function useCreateEmTemplate() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async (body: any) => { const { data } = await api.post(`${BASE}/templates`, body); return data }, onSuccess: () => qc.invalidateQueries({ queryKey: ['em-templates'] }) })
}
export function useUpdateEmTemplate() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async ({ id, ...body }: any) => { const { data } = await api.put(`${BASE}/templates/${id}`, body); return data }, onSuccess: () => { qc.invalidateQueries({ queryKey: ['em-templates'] }); qc.invalidateQueries({ queryKey: ['em-template'] }) } })
}
export function useDeleteEmTemplate() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async (id: string) => { await api.delete(`${BASE}/templates/${id}`) }, onSuccess: () => qc.invalidateQueries({ queryKey: ['em-templates'] }) })
}
export function useDuplicateEmTemplate() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async (id: string) => { const { data } = await api.post(`${BASE}/templates/${id}/duplicate`); return data }, onSuccess: () => qc.invalidateQueries({ queryKey: ['em-templates'] }) })
}
export function usePreviewEmTemplate() {
  return useMutation({ mutationFn: async (id: string) => { const { data } = await api.post(`${BASE}/templates/${id}/preview`); return data } })
}
export function useSendTestEmail() {
  return useMutation({ mutationFn: async ({ id, email_to, account_id, prefix }: { id: string; email_to: string; account_id?: string; prefix?: boolean }) => { const { data } = await api.post(`${BASE}/templates/${id}/send-test`, null, { params: { email_to, account_id, prefix } }); return data } })
}

// Contact Lists
export function useEmLists() {
  return useQuery({ queryKey: ['em-lists'], queryFn: async () => { const { data } = await api.get<EmContactList[]>(`${BASE}/lists`); return data }, staleTime: 30000 })
}
export function useCreateEmList() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async (body: any) => { const { data } = await api.post(`${BASE}/lists`, body); return data }, onSuccess: () => qc.invalidateQueries({ queryKey: ['em-lists'] }) })
}
export function useUpdateEmList() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async ({ id, ...body }: any) => { const { data } = await api.put(`${BASE}/lists/${id}`, body); return data }, onSuccess: () => qc.invalidateQueries({ queryKey: ['em-lists'] }) })
}
export function useDeleteEmList() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async (id: string) => { await api.delete(`${BASE}/lists/${id}`) }, onSuccess: () => qc.invalidateQueries({ queryKey: ['em-lists'] }) })
}

// Contacts
export interface EmContactPage {
  items: EmContact[]
  total: number
  page: number
  per_page: number
  total_pages: number
}
export function useEmContacts(listId: string, params?: { search?: string; page?: number; per_page?: number }) {
  return useQuery({ queryKey: ['em-contacts', listId, params], queryFn: async () => { const { data } = await api.get<EmContactPage>(`${BASE}/lists/${listId}/contacts`, { params }); return data }, enabled: !!listId, staleTime: 15000 })
}
export function useCreateEmContact() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async ({ listId, ...body }: any) => { const { data } = await api.post(`${BASE}/lists/${listId}/contacts`, body); return data }, onSuccess: () => { qc.invalidateQueries({ queryKey: ['em-contacts'] }); qc.invalidateQueries({ queryKey: ['em-lists'] }) } })
}
export function useDeleteEmContact() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async ({ listId, contactId }: { listId: string; contactId: string }) => { await api.delete(`${BASE}/lists/${listId}/contacts/${contactId}`) }, onSuccess: () => { qc.invalidateQueries({ queryKey: ['em-contacts'] }); qc.invalidateQueries({ queryKey: ['em-lists'] }) } })
}

// Campaigns
export function useEmCampaigns(params?: { status?: string; search?: string; page?: number; per_page?: number }) {
  return useQuery({ queryKey: ['em-campaigns', params], queryFn: async () => { const { data } = await api.get(`${BASE}/campaigns`, { params }); return data as { items: EmCampaign[]; total: number; page: number; per_page: number; total_pages: number } }, staleTime: 15000 })
}
export function useEmCampaign(id: string) {
  return useQuery({ queryKey: ['em-campaign', id], queryFn: async () => { const { data } = await api.get<EmCampaign>(`${BASE}/campaigns/${id}`); return data }, enabled: !!id })
}
export function useCreateEmCampaign() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async (body: any) => { const { data } = await api.post(`${BASE}/campaigns`, body); return data }, onSuccess: () => qc.invalidateQueries({ queryKey: ['em-campaigns'] }) })
}
export function useUpdateEmCampaign() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async ({ id, ...body }: any) => { const { data } = await api.put(`${BASE}/campaigns/${id}`, body); return data }, onSuccess: () => { qc.invalidateQueries({ queryKey: ['em-campaigns'] }); qc.invalidateQueries({ queryKey: ['em-campaign'] }) } })
}
export function useDeleteEmCampaign() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async (id: string) => { await api.delete(`${BASE}/campaigns/${id}`) }, onSuccess: () => qc.invalidateQueries({ queryKey: ['em-campaigns'] }) })
}
export function useScheduleEmCampaign() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async (id: string) => { const { data } = await api.post(`${BASE}/campaigns/${id}/schedule`); return data }, onSuccess: () => { qc.invalidateQueries({ queryKey: ['em-campaigns'] }); qc.invalidateQueries({ queryKey: ['em-campaign'] }) } })
}
export function usePauseEmCampaign() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async (id: string) => { const { data } = await api.post(`${BASE}/campaigns/${id}/pause`); return data }, onSuccess: () => { qc.invalidateQueries({ queryKey: ['em-campaigns'] }); qc.invalidateQueries({ queryKey: ['em-campaign'] }) } })
}
export function useResumeEmCampaign() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async (id: string) => { const { data } = await api.post(`${BASE}/campaigns/${id}/resume`); return data }, onSuccess: () => { qc.invalidateQueries({ queryKey: ['em-campaigns'] }); qc.invalidateQueries({ queryKey: ['em-campaign'] }) } })
}
export function useEmCampaignLogs(campaignId: string, params?: { status?: string; page?: number; per_page?: number }) {
  return useQuery({ queryKey: ['em-campaign-logs', campaignId, params], queryFn: async () => { const { data } = await api.get<EmCampaignLog[]>(`${BASE}/campaigns/${campaignId}/logs`, { params }); return data }, enabled: !!campaignId })
}

// Recent Logs (all campaigns)
export function useEmRecentLogs(params?: { campaign_id?: string; status?: string; search?: string; page?: number; per_page?: number }) {
  return useQuery({ queryKey: ['em-recent-logs', params], queryFn: async () => { const { data } = await api.get<EmCampaignLog[]>(`${BASE}/logs/recent`, { params }); return data }, staleTime: 15000 })
}
export function useEmRecentWaLogs(params?: { campaign_id?: string; status?: string; search?: string; page?: number; per_page?: number }) {
  return useQuery({ queryKey: ['em-recent-wa-logs', params], queryFn: async () => { const { data } = await api.get<EmWhatsAppLog[]>(`${BASE}/whatsapp-logs/recent`, { params }); return data }, staleTime: 15000 })
}

// WhatsApp Accounts
export function useEmWaAccounts() {
  return useQuery({ queryKey: ['em-wa-accounts'], queryFn: async () => { const { data } = await api.get<EmWhatsAppAccount[]>(`${BASE}/whatsapp/accounts`); return data }, staleTime: 60000 })
}
export function useCreateEmWaAccount() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async (body: any) => { const { data } = await api.post(`${BASE}/whatsapp/accounts`, body); return data }, onSuccess: () => qc.invalidateQueries({ queryKey: ['em-wa-accounts'] }) })
}
export function useUpdateEmWaAccount() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async ({ id, ...body }: any) => { const { data } = await api.put(`${BASE}/whatsapp/accounts/${id}`, body); return data }, onSuccess: () => qc.invalidateQueries({ queryKey: ['em-wa-accounts'] }) })
}
export function useTestEmWaAccount() {
  return useMutation({ mutationFn: async (id: string) => { const { data } = await api.post(`${BASE}/whatsapp/accounts/${id}/test`); return data } })
}

// CSV Import
export function useUploadCsv() {
  return useMutation({ mutationFn: async ({ listId, file }: { listId: string; file: File }) => {
    const form = new FormData(); form.append('file', file)
    const { data } = await api.post<CsvUploadResponse>(`${BASE}/lists/${listId}/import/upload`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return data
  }})
}

// Bulk actions
export function useBulkDeleteEmContacts() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ listId, contactIds }: { listId: string; contactIds: string[] }) => {
      const { data } = await api.post(`${BASE}/lists/${listId}/contacts/bulk-delete`, contactIds)
      return data
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['em-contacts'] }); qc.invalidateQueries({ queryKey: ['em-lists'] }) }
  })
}

export function useBulkUpdateCurva() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ listId, contactIds, curva }: { listId: string; contactIds: string[]; curva: string }) => {
      const { data } = await api.post(`${BASE}/lists/${listId}/contacts/bulk-update-curva?curva_abc=${curva}`, contactIds)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['em-contacts'] })
  })
}

export function useConvertToLead() {
  return useMutation({
    mutationFn: async ({ listId, contactId }: { listId: string; contactId: string }) => {
      const { data } = await api.post(`${BASE}/lists/${listId}/contacts/${contactId}/convert-to-lead`)
      return data
    }
  })
}

// Update contact
export function useUpdateEmContact() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ listId, contactId, ...body }: any) => {
      const { data } = await api.put(`${BASE}/lists/${listId}/contacts/${contactId}`, body)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['em-contacts'] })
  })
}

// ──────────────────────────────────────────────────────────────
//  CRM contact searchable (for Email Marketing import modal)
// ──────────────────────────────────────────────────────────────

export interface CrmContactWithLead {
  id: string
  first_name: string
  last_name: string | null
  email: string | null
  phone: string | null
  whatsapp: string | null
  job_title: string | null
  company_id: string
  company_name: string | null
  company_cnpj: string | null
  lead_id: string | null
  lead_title: string | null
  lead_origin: string | null
  lead_sub_origin: string | null
  lead_owner_id: string | null
  lead_owner_name: string | null
}

export interface CrmContactSearchableList {
  items: CrmContactWithLead[]
  total: number
  page: number
  per_page: number
  pages: number
}

export interface CrmContactFilters {
  page?: number
  per_page?: number
  search?: string
  lead_title?: string
  company_name?: string
  origin?: string
  sub_origin?: string
  owner_id?: string
  only_with_email?: boolean
}

export function useSearchableCrmContacts(filters: CrmContactFilters, enabled = true) {
  return useQuery({
    queryKey: ['crm-contacts-searchable', filters],
    queryFn: async () => {
      const params: Record<string, unknown> = {}
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') params[k] = v
      })
      const { data } = await api.get<CrmContactSearchableList>('/v1/contacts/searchable', { params })
      return data
    },
    enabled,
    staleTime: 10_000,
  })
}

export interface ImportFromCrmResponse {
  list_id: string
  requested: number
  inserted: number
  skipped_no_email: number
  skipped_duplicate: number
  skipped_not_found: number
}

export function useImportContactsFromCrm() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ listId, contactIds }: { listId: string; contactIds: string[] }) => {
      const { data } = await api.post<ImportFromCrmResponse>(
        `/v1/email-marketing/lists/${listId}/contacts/import-from-crm`,
        { contact_ids: contactIds },
      )
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['em-lists'] })
      qc.invalidateQueries({ queryKey: ['em-contacts'] })
    },
  })
}

export interface CrmFacetOwner {
  id: string
  name: string
  lead_count: number
}

export interface CrmSearchableFacets {
  sub_origins: string[]
  owners: CrmFacetOwner[]
  has_null_sub_origin: boolean
}

export function useSearchableCrmFacets(enabled = true) {
  return useQuery({
    queryKey: ['crm-contacts-searchable-facets'],
    queryFn: async () => {
      const { data } = await api.get<CrmSearchableFacets>('/v1/contacts/searchable/facets')
      return data
    },
    enabled,
    staleTime: 30_000,
  })
}

